import type { AnalyticsQuery } from "schemas/generated/analyticsRead";

import * as React from "react";

import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import { sanitizePageUrlPath } from "@v1/features/analytics/analytics";
import useBasicAnalyticsRead from "@v1/features/analytics/useBasicAnalyticsRead";
import { useOverridableInput } from "@v1/hooks/useOverridableInput";
import { DEFAULT_FILTERS } from "@v2/features/analytics/constants";
import { AppWindow } from "lucide-react";

import { useAnalyticsQueryContext } from "../contexts/AnalyticsQueryContext";

export const AnalyticsHostPagesCombobox: React.FC = () => {
  const { query, dispatchAnalyticsQuery } = useAnalyticsQueryContext();

  // NOTE (Max, 2024-12-15): pageUrlPath is just a filter, so we find it
  // in the context's filters. The only time we use "equals"
  // is when on the deep dive page, so that's the one we're looking for.
  const pageUrlPath = React.useMemo(() => {
    return (
      query.filters.urlPath.find((filter) => filter.operator === "equals")
        ?.value[0] ?? ""
    );
  }, [query.filters.urlPath]);

  const handlePageUrlPathChange = (value: string) => {
    dispatchAnalyticsQuery({
      type: "filters.replaceUrlPath",
      payload: value,
    });
  };

  const [searchTerm, setSearchTerm] = React.useState("");

  const { mainRangeResults, isLoading } = useBasicAnalyticsRead({
    type: "withUrlSyncQuery",
    queryConfig: {
      useChartInterval: false,
      queryOverrides: {
        // NOTE (Max, 2024-12-15): Requirement from product team to only show top 10 results
        // by unique_sessions in this combobox
        limit: 10,
        sortMetric: "unique_sessions" as AnalyticsQuery["sortMetric"],
        order: "DESC" as AnalyticsQuery["order"],
        /**
         * NOTE (Max, 2024-12-15): As this Combobox is shown in the Deep Dive page, the urlPath
         * filters will lead to results for only that 1 urlPath. Given that we want to show
         * all the urlPaths in the Combobox options, we're overriding the context's filters so that
         * only urlPaths containing the searchTerm are returned.
         */
        filters: {
          ...query.filters,
          urlPath: [
            ...DEFAULT_FILTERS.urlPath,
            { operator: "contains", value: searchTerm },
          ],
        },
      },
    },
  });

  const { value: inputValue, onChange: onInputChange } = useOverridableInput({
    value: searchTerm,
    onValueChange: (value: string) => {
      setSearchTerm(value);
    },
  });

  const pageOptions = React.useMemo(() => {
    return mainRangeResults.map((result) => ({
      value: result.urlPath,
      label: sanitizePageUrlPath(result.urlPath),
    }));
  }, [mainRangeResults]);

  return (
    <Combobox
      options={pageOptions}
      value={sanitizePageUrlPath(pageUrlPath)}
      onChange={handlePageUrlPathChange}
      input={inputValue}
      onInputChange={(value: string) => {
        onInputChange({
          target: { value },
        } as React.ChangeEvent<HTMLInputElement>);
      }}
      areOptionsSearchable
      loader={
        isLoading ? (
          <Spinner className="size-7 mx-auto" variant="primary" />
        ) : null
      }
      trigger={
        <Combobox.SelectionButton
          startEnhancer={<AppWindow className="h-4 w-4" />}
          title={sanitizePageUrlPath(pageUrlPath) ?? "Select a page..."}
          isPlaceholder={!pageUrlPath}
        />
      }
    />
  );
};
